import { useEffect, useRef } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  BridgeEventStream,
  BridgeSessionSource,
  ShogunChopPushFrameStream,
  browserWebSocketFactory,
  createDaemonCatalogRegistrar,
  registerPlatformCallExecutor,
  unregisterPlatformCallExecutor,
  type FeedRegistrar,
  type NotificationRegistrar,
  type ShogunChopResource,
} from '@localagent/sdk';
import { toast } from '@localagent/ui/components/sonner';
import { getSDK } from '../services/sdk-instance';
import { useShogunchop } from '../providers/ShogunChopProvider';
import { holdsIdentityAtom, claimsAtom, tokenAtom, authMaterialAtom } from '../stores/auth/atoms';
import {
  apiBaseUrlAtom,
  bridgeEventStreamAtom,
  daemonPushFrameStreamAtom,
} from '../stores/sdk/atoms';
import {
  createPlatformCallExecutor,
  dispatchAllowlistedCall,
  type ConfirmFn,
} from '../shogun/platformCallExecutor';
import { createNotificationCursorStore } from '@localagent/sdk';

async function runClaimedPlatformCall(
  sc: ShogunChopResource,
  requestId: string,
  call: { resource: string; method: string; arguments: Record<string, unknown> },
): Promise<void> {
  if (!(await sc.claimPlatformCall(requestId))) return;
  let outcome: { result: unknown } | { error: string };
  try {
    const dispatched = await dispatchAllowlistedCall(getSDK(), call);
    outcome = dispatched.status === 'ok' ? { result: dispatched.result } : { error: dispatched.error };
  } catch (err: unknown) {
    outcome = { error: err instanceof Error ? err.message : String(err) };
  }
  await sc.postPlatformCallResult(requestId, outcome);
}

export function useWebBridgeClient(confirm: ConfirmFn): void {
  const shogunchop = useShogunchop();
  // Dials in every identity-holding phase, `degraded` included (sdk ADR-012).
  const holdsIdentity = useAtomValue(holdsIdentityAtom);
  const apiBaseUrl = useAtomValue(apiBaseUrlAtom);
  const claims = useAtomValue(claimsAtom);
  const token = useAtomValue(tokenAtom);
  const authMaterial = useAtomValue(authMaterialAtom);
  const setBridgeStream = useSetAtom(bridgeEventStreamAtom);
  const setDaemonPushStream = useSetAtom(daemonPushFrameStreamAtom);
  const streamRef = useRef<BridgeEventStream | null>(null);

  const orgId = claims?.orgId ?? null;
  const accountId = claims?.accountId ?? null;
  const hasToken = Boolean(token);

  useEffect(() => {
    let effectCancelled = false;
    if (shogunchop.resource) return;
    if (!holdsIdentity) return;
    if (!apiBaseUrl || !token || !orgId || !accountId) return;
    // A daemon owns the device's one bridge (bridge ADR-001); a daemon-less shell takes the browser arm (sdk ADR-031).
    if (getSDK().hasDaemon) return;

    const globalWs =
      typeof window !== 'undefined'
        ? (window as { WebSocket?: typeof WebSocket }).WebSocket
        : undefined;
    if (!globalWs) return;

    const sdk = getSDK();
    // Asked of the SDK inside the effect — the identity is the SDK's answer,
    // never an atom snapshot (sdk ADR-009).
    void (async () => {
    const deviceId = await sdk.deviceId();
    if (effectCancelled) return;

    const executor = createPlatformCallExecutor(sdk, confirm);
    registerPlatformCallExecutor((call) => executor.execute(call));

    const registrar = sdk.chat.createSessionRegistrar();
    // Notification push control plane on the SAME connection — the stream
    // (re)subscribes on every (re)connect with its keyset cursor so a reconnect
    // drains the durable error/notification backlog.
    const notificationRegistrar: NotificationRegistrar = {
      subscribe: (dev, after) => sdk.notifications.subscribe(dev, after),
      unsubscribe: (dev) => sdk.notifications.unsubscribe(dev),
    };
    const catalogRegistrar = {
      subscribe: (dev: string, scopeType: string, scopeId: string) =>
        sdk.catalogs.subscribe(dev, scopeType, scopeId),
      unsubscribe: (dev: string, scopeType: string, scopeId: string) =>
        sdk.catalogs.unsubscribe(dev, scopeType, scopeId),
    };
    const feedRegistrar: FeedRegistrar = {
      subscribe: (dev) => sdk.feed.subscribe({ device_id: dev }),
    };
    const stream = new BridgeEventStream(
      apiBaseUrl,
      { deviceId, organizationId: orgId, accountId, token },
      browserWebSocketFactory,
      registrar,
      notificationRegistrar,
      sdk.workspaces,
      createNotificationCursorStore(getSDK().storage!, accountId, deviceId),
      catalogRegistrar,
      feedRegistrar
    );
    if (effectCancelled) {
      stream.close();
      return;
    }
    streamRef.current = stream;

    stream.onError = (err) => {
      console.error('[web-bridge] session-event stream error', err);
    };
    const source = new BridgeSessionSource(stream, registrar);
    sdk.chat.setBridgeSessionSource(source);

    setBridgeStream(stream);
    })();

    return () => {
      effectCancelled = true;
      const live = streamRef.current;
      streamRef.current = null;
      setBridgeStream(null);
      try {
        getSDK().chat.setBridgeSessionSource(null);
      } catch {
        // SDK uninstalled mid-cleanup — nothing to detach.
      }
      live?.close();
      unregisterPlatformCallExecutor();
    };
    // hasToken (not token): see note above. `token` is read for the initial
    // identify URL when hasToken flips true; renews go through updateAuthToken.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- token value: see note
  }, [
    shogunchop.resource,
    holdsIdentity,
    apiBaseUrl,
    hasToken,
    orgId,
    accountId,
    confirm,
    setBridgeStream,
  ]);

  // Rotation reaches the stream through the holder phase: authMaterialAtom is
  // projected off the SDK credential holder, so a renewal lands here without a
  // second token watcher (sdk ADR-010).
  useEffect(() => {
    const material = authMaterial;
    if (material) streamRef.current?.updateAuthToken(material.session_jwt);
  }, [authMaterial]);

  useEffect(() => {
    const sc = shogunchop.resource;
    if (!sc) return; // web — handled by the arm above.
    if (!holdsIdentity) return;

    const stream = sc.sharedEvents();

    stream.on((event) => {
      const e = event as { event?: string; data?: Record<string, unknown> };
      if (e.event !== 'shogun:platform_call' || !e.data) return;
      const data = e.data as {
        request_id?: string;
        resource?: string;
        method?: string;
        arguments?: Record<string, unknown>;
      };
      const { request_id: requestId, resource, method } = data;
      if (!requestId || !resource || !method) return;

      // (shogunchop ADR-61486)
      void runClaimedPlatformCall(sc, requestId, {
        resource,
        method,
        arguments: data.arguments ?? {},
      }).catch((err: unknown) => {
        const reason = err instanceof Error ? err.message : String(err);
        console.error(`[platform-call] ${resource}.${method} (${requestId}) failed`, err);
        toast.error(`Shogun's ${resource}.${method} request could not run on this computer`, {
          description: reason,
        });
      });
    });

    return () => {
      stream.release();
    };
  }, [shogunchop.resource, holdsIdentity]);

  // The daemon owns this device's ONE cloud bridge connection (bridge
  // ADR-001/004), so the renderer's replay floor rides its loopback relay
  // rather than a second socket (events ADR-012).
  useEffect(() => {
    const sc = shogunchop.resource;
    if (!sc) return;
    if (!holdsIdentity) return;
    if (!accountId) return;

    let cancelled = false;
    let pushStream: ShogunChopPushFrameStream | null = null;
    let relay: ReturnType<typeof sc.sharedEvents> | null = null;
    // Asked of the SDK per attempt — the daemon's id moves during sign-in.
    void (async () => {
      const deviceId = await getSDK().deviceId();
      if (cancelled) return;
      relay = sc.sharedEvents();
      const notificationRegistrar: NotificationRegistrar = {
        subscribe: (dev, after) => getSDK().notifications.subscribe(dev, after),
        unsubscribe: (dev) => getSDK().notifications.unsubscribe(dev),
      };
      pushStream = new ShogunChopPushFrameStream(
        relay,
        deviceId,
        notificationRegistrar,
        (err) => {
          console.error('[daemon-push] notification subscribe failed', err);
        },
        createNotificationCursorStore(getSDK().storage!, accountId, deviceId),
        // The daemon owns desktop catalog holds, counted per /events socket (shogunchop ADR-ab462).
        createDaemonCatalogRegistrar(relay),
        { subscribe: (dev) => getSDK().feed.subscribe({ device_id: dev }) },
      );
      setDaemonPushStream(pushStream);
    })();

    return () => {
      cancelled = true;
      setDaemonPushStream(null);
      pushStream?.close();
      relay?.release();
    };
  }, [shogunchop.resource, holdsIdentity, accountId, setDaemonPushStream]);
}
